<template>
  <section>
    <div class="form-header">
      <h1>Reset your Password</h1>
      <br>
      <h3>Step 1: Verify your information</h3>
    </div>
    <hr>
    <div>
      <b-form @submit="onSubmit" v-if="show" autocomplete="off">
        <div id="rules">
          <div>
            <a href="#" @click.prevent="macPasswordReset">Mac users click here for instructions</a>
          </div>
          <br>
          <p>{{ text }}</p>
        </div>
        <br>
        <b-form-group id="userIDLabel">
          <b-form-input id="userID"
                        type="text"
                        v-model="userId"
                        required
                        placeholder="Username / Employee Number">
          </b-form-input>

          <br>

          <CommonReCaptcha
              ref="captchaRef"
              @verify="onCaptchaVerify"
          />

        </b-form-group>
        <div>
          <b-button type="submit" variant="primary">Next</b-button>
          <Spinner v-if="spinner" />
        </div>
      </b-form>

      <b-modal ref="alertModal" :title="alertHeader" :ok-only="true">
        <p class="my-4">{{ alertText }}</p>
        <template #footer>
          <div class="w-100">
            <b-button v-if="alertNav === 'home'" size="sm" class="float-end" variant="primary" @click="navigateHome()">
              Okay
            </b-button>
            <b-button v-if="alertNav === 'resetPassword'" size="sm" class="float-end" variant="primary" @click="navigateResetPassword()">
              Okay
            </b-button>
            <b-button v-if="alertNav === 'here'" size="sm" class="float-end" variant="primary" @click="navigateHere()">
              Okay
            </b-button>
          </div>
        </template>
      </b-modal>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import CommonReCaptcha from '../../components/CommonReCaptcha.vue';
import Spinner from '../Spinner.vue';
import { useFormDataStore } from '../../stores/formData';
import { useLocalApiStore } from '../../stores/localApi';
import { useGoogleStore } from '../../stores/google';
import { useFormOrchestrationStore } from '../../stores/formOrchestration';

// --- Store & Router Initialization ---
const router = useRouter();
const formDataStore = useFormDataStore();
const localApiStore = useLocalApiStore();
const googleStore = useGoogleStore();
const orchestrationStore = useFormOrchestrationStore();

// --- Reactive State ---
const captchaRef = ref(null);
const captchaResponse = ref('');
const alertModal = ref(null);
const alertHeader = ref('');
const alertText = ref('');
const alertNav = ref('here');
const text = ref('Please enter your username or employee number:');
const show = ref(true);
const spinner = ref(false);

// --- Computed ---
const getSessionKey = computed(() => formDataStore.getSessionKey);
const userId = computed({
  get: () => formDataStore.formData.userId,
  set: (value) => formDataStore.updateFormDataUserId(value)
});

// --- Methods ---
const onCaptchaVerify = (token) => {
  captchaResponse.value = token;
};

const macPasswordReset = () => {
  router.push('/macPasswordReset');
};

const resetCaptcha = () => {
  // We now call the public reset method on our child component
  captchaRef.value?.reset();
  captchaResponse.value = '';
};

const showAlertModal = (header, textVal, nav) => {
  alertNav.value = nav;
  alertHeader.value = header;
  alertText.value = textVal;
  alertModal.value?.show();
};

const hideAlertModal = () => {
  resetCaptcha();
  alertModal.value?.hide();
};

const navigateHome = () => {
  hideAlertModal();
  formDataStore.clearData();
  router.push('/');
};

const navigateResetPassword = () => {
  hideAlertModal();
  formDataStore.clearData();
  router.push('/resetPassword');
};

const navigateHere = () => {
  hideAlertModal();
};

const onSubmit = async (evt) => {
  evt.preventDefault();
  spinner.value = true;

  if (captchaResponse.value !== '') {
    const captchaParams = {
      params: {
        sessionKey: getSessionKey.value,
        response: captchaResponse.value
      }
    };

    const captchaValidation = await googleStore.checkCaptcha(captchaParams);

    if (captchaValidation === true) {
      const parameters = {
        userId: userId.value,
        sessionKey: getSessionKey.value,
        requestType: 'passwordReset'
      };

      const accountData = await localApiStore.accountLookup(parameters);

      if (accountData.result === 'FAILURE' || accountData.result === 'Failure') {
        if (accountData.resultMessage === 'Invalid Session Encountered') {
          // Refresh session key in the central formData store so all steps use the new key
          const newKey = await localApiStore.newSessionKey();
          formDataStore.setSessionKey(newKey);
          // Reset captcha and allow the user to retry immediately on Step 1
          resetCaptcha();
          spinner.value = false;
          showAlertModal('Session Refreshed', 'Your session expired and has been renewed. Please try again.', 'here');
          return;
        }
        spinner.value = false;
        showAlertModal('Request Failure', accountData.resultMessage || 'General Failure', 'home');
      } else {
        formDataStore.updateValidationDevices(accountData.data.validationDevices);
        spinner.value = false;
        orchestrationStore.updateCurrentForm('FormPWR2');
      }
    } else {
      spinner.value = false;
      showAlertModal('Error', 'Verification failed. Please contact the Help Desk.', 'home');
    }
  } else {
    spinner.value = false;
    showAlertModal('Error', "Please validate you're not a robot and try again.", 'here');
  }
};

onMounted(async () => {
  let sessionKey = getSessionKey.value;
  if (sessionKey == null || sessionKey.length < 12) {
    // Acquire a fresh session key through the API store and persist it to the shared form data store
    const newKey = await localApiStore.newSessionKey();
    formDataStore.setSessionKey(newKey);
  }
});
</script>

<style scoped>

.form-header {
  display: flex;
  flex-direction: column; /* This stacks h1 and h3 vertically */
  justify-content: flex-start;
  align-items: flex-start; /* Aligns text to the left */
  margin: 25px 0;
  width: 100%;
}

h1 { font-weight: bold; color: #000; }
h3 { color: #4A4A4A; }
form { display: flex; flex-direction: column; }
.form-group {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-weight: bold;
  color: #000;
}
button {
  margin: 15px 0px;
  background-color: #5487BA;
  width: 100px;
  font-weight: bold;
}
button:hover { background-color: #123A5D; }
.form-control { background-color: #FFF; }
#rules {
  background-color: #f5f5f5;
  width: 100%;
  font-size: 1rem;
  padding: 10px;
  text-align: left;
}
</style>